import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Divider,
  App,
} from 'antd';
import {
  ArrowLeftOutlined,
  GlobalOutlined,
  InstagramOutlined,
  TwitterOutlined,
  StarOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  fetchBrand,
  fetchBrandRating,
  clearCurrentBrand,
} from '../store/slices/brandSlice';
import { apiClient } from '../services/api';
import RatingDisplay from '../components/RatingDisplay';
import FavoriteButton from '../components/FavoriteButton';
import { useBrowsingTracker } from '../hooks/useBrowsingTracker';
import Loading from '../components/common/Loading';
import ErrorState from '../components/common/ErrorState';
import EvidenceSection from '../components/brands/EvidenceSection';
import BrandComparison from '../components/BrandComparison';
import UserReviews from '../components/UserReviews';
import CertificationBadges from '../components/CertificationBadges';
import EnhancedRadarChart from '../components/EnhancedRadarChart';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import PurchaseEntryCard from '../components/brands/PurchaseEntryCard';
import Seo from '../components/seo/Seo';

const { Title, Paragraph } = Typography;

const BrandDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentBrand, currentRating, loading, error } = useAppSelector(
    state => state.brand
  );

  const [similarBrands, setSimilarBrands] = useState<any[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [comparisonVisible, setComparisonVisible] = useState(false);
  const [comparisonBrands, setComparisonBrands] = useState<any[]>([]);
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [mapCountry, setMapCountry] = useState<string | null>(null);

  const brandId = id ? parseInt(id, 10) : undefined;

  // 使用浏览记录Hook
  useBrowsingTracker(brandId);

  useEffect(() => {
    if (brandId) {
      dispatch(fetchBrand(brandId));
      dispatch(fetchBrandRating(brandId));
      loadSimilarBrands(brandId);
    }

    return () => {
      dispatch(clearCurrentBrand());
    };
  }, [brandId, dispatch]);

  // 简单从总部字段提取国家名称，与后端 geo 路由保持一致的解析方式
  const extractCountryName = (headquarters?: string | null): string | null => {
    if (!headquarters) return null;
    const parts = headquarters
      .split(/[,|]/)
      .map(s => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return null;
    let name = parts[parts.length - 1];
    if (/^usa$/i.test(name) || /^united states of america$/i.test(name))
      name = 'United States';
    if (
      /^uk$/i.test(name) ||
      /^england$/i.test(name) ||
      /^britain$/i.test(name)
    )
      name = 'United Kingdom';
    if (/^prc$/i.test(name) || /^china$/i.test(name)) name = 'China';
    return name;
  };

  // 加载国家中心点
  useEffect(() => {
    const loadCountryCenter = async () => {
      if (!currentBrand?.headquarters) return;
      const country = extractCountryName(currentBrand.headquarters);
      if (!country) return;
      try {
        const res = await apiClient.get<{
          success: boolean;
          data: Array<{
            country: string;
            code: string | null;
            lat: number | null;
            lng: number | null;
          }>;
        }>('/geo/brands/summary');
        const entry = (res.data || []).find(
          x => x.country.toLowerCase() === country.toLowerCase()
        );
        if (entry && entry.lat != null && entry.lng != null) {
          setMapCenter([entry.lat, entry.lng]);
          setMapCountry(entry.country);
        } else {
          setMapCenter(null);
          setMapCountry(country);
        }
      } catch (e) {
        // 地图非关键阻塞，无需报错打断
        setMapCenter(null);
      }
    };
    loadCountryCenter();
  }, [currentBrand?.headquarters]);

  const markerIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl:
      'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    shadowSize: [41, 41],
  });

  const loadSimilarBrands = async (brandId: number) => {
    setSimilarLoading(true);
    try {
      const response = await apiClient.getSimilarBrands(brandId, 3);
      setSimilarBrands(response.data);
    } catch (error) {
      message.error(t('common.error'));
    } finally {
      setSimilarLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/brands');
  };

  const handleViewDetails = (brandId: number) => {
    navigate(`/brands/${brandId}`);
  };

  const handleCompareClick = () => {
    if (currentBrand) {
      setComparisonBrands([currentBrand]);
      setComparisonVisible(true);
    }
  };

  if (loading) {
    return (
      <Loading
        tip={t('brand_detail.loading_brand', {
          defaultValue: 'Loading brand...',
        })}
      />
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <ErrorState
          title={t('brand_detail.load_failed', { defaultValue: 'Load failed' })}
          subtitle={error}
          onRetry={() => {
            if (brandId) {
              dispatch(fetchBrand(brandId));
              dispatch(fetchBrandRating(brandId));
            }
          }}
        />
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button size="small" onClick={handleBack}>
            {t('brand_detail.back_to_list')}
          </Button>
        </div>
      </div>
    );
  }

  if (!currentBrand) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <p>
          {t('brand_detail.not_found', { defaultValue: 'Brand not found' })}
        </p>
        <Button onClick={handleBack}>{t('brand_detail.back_to_list')}</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Seo
        title={currentBrand.name}
        description={currentBrand.description || undefined}
        path={`/brands/${id}`}
        alternates={[
          { hrefLang: 'en', href: `${(import.meta as any).env.VITE_SITE_URL || ''}/brands/${id}?lng=en` },
          { hrefLang: 'zh-CN', href: `${(import.meta as any).env.VITE_SITE_URL || ''}/brands/${id}?lng=zh-CN` },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Brand',
          name: currentBrand.name,
          url: currentBrand.website || undefined,
          logo: currentBrand.logo || undefined,
          description: currentBrand.description || undefined
        }}
      />
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        {t('brand_detail.back_to_list')}
      </Button>

      <Row gutter={24}>
        <Col span={24}>
          <Card>
            {/* 品牌Logo展示：优先显示后端提供的 logo 静态路径（如 /brands/xxx.png） */}
            {currentBrand.logo && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '8px 0 16px',
              }}>
                <div style={{
                  width: 120,
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  overflow: 'hidden'
                }}>
                  <img
                    src={currentBrand.logo}
                    alt={`${currentBrand.name} Logo`}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    loading="lazy"
                  />
                </div>
                <Title level={1} style={{ margin: 0 }}>{currentBrand.name}</Title>
              </div>
            )}
            {!currentBrand.logo && (
              <Title level={1}>{currentBrand.name}</Title>
            )}
            <Paragraph style={{ fontSize: '16px', marginBottom: 16 }}>
              {currentBrand.description}
            </Paragraph>

            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <strong>{t('brand_detail.founded')}:</strong>{' '}
                {currentBrand.founded
                  ? new Date(currentBrand.founded).getFullYear()
                  : '--'}
              </Col>
              <Col span={6}>
                <strong>{t('brand_detail.headquarters')}:</strong>{' '}
                {currentBrand.headquarters}
              </Col>
              <Col span={6}>
                <Space>
                  {currentBrand.website && (
                    <Button
                      type="link"
                      icon={<GlobalOutlined />}
                      href={currentBrand.website}
                      target="_blank"
                    >
                      {t('brand_detail.website')}
                    </Button>
                  )}
                  {currentBrand.socialMedia?.instagram && (
                    <Button
                      type="link"
                      icon={<InstagramOutlined />}
                      href={currentBrand.socialMedia.instagram}
                      target="_blank"
                    >
                      Instagram
                    </Button>
                  )}
                  {currentBrand.socialMedia?.twitter && (
                    <Button
                      type="link"
                      icon={<TwitterOutlined />}
                      href={currentBrand.socialMedia.twitter}
                      target="_blank"
                    >
                      X
                    </Button>
                  )}
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <FavoriteButton brandId={currentBrand.id} />
                  <Button
                    type="primary"
                    icon={<StarOutlined />}
                    onClick={() => navigate(`/brands/${Number(id)}/rate`)}
                    block
                  >
                    {t('brand_detail.rate_this_brand')}
                  </Button>
                  <Button
                    icon={<GlobalOutlined />}
                    onClick={() => {
                      const country = extractCountryName(
                        currentBrand.headquarters
                      );
                      if (mapCenter && country) {
                        const [lat, lng] = mapCenter;
                        navigate(
                          `/world-map?name=${encodeURIComponent(country)}&lat=${lat}&lng=${lng}`
                        );
                      } else if (country) {
                        navigate(
                          `/world-map?name=${encodeURIComponent(country)}`
                        );
                      } else {
                        navigate('/world-map');
                      }
                    }}
                    block
                  >
                    {t('brand_detail.view_on_map')}
                  </Button>
                  <Button
                    icon={<SwapOutlined />}
                    onClick={handleCompareClick}
                    block
                  >
                    {t('brand_detail.compare_brands')}
                  </Button>
                </Space>
              </Col>
            </Row>

            <div style={{ marginBottom: 16 }}>
              <strong>{t('brand_detail.business_categories')}:</strong>
              <div style={{ marginTop: 8 }}>
                {currentBrand.categories.map(category => (
                  <Tag
                    key={category}
                    style={{
                      color: 'var(--app-primary-color)',
                      borderColor: 'var(--app-primary-color)',
                      backgroundColor: 'transparent',
                      marginBottom: 4,
                    }}
                  >
                    {category}
                  </Tag>
                ))}
              </div>
            </div>

            {/* 品牌地图（按国家中心点定位） */}
            {mapCenter && (
              <div style={{ marginTop: 16 }}>
                <strong>
                  {t('brand_detail.brand_map')}
                  {mapCountry ? `（${mapCountry}）` : ''}:
                </strong>
                <div style={{ height: 320, width: '100%', marginTop: 8 }}>
                  <MapContainer
                    center={mapCenter}
                    zoom={5}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={mapCenter} icon={markerIcon}>
                      <Popup>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {currentBrand.name}
                          </div>
                          <div>
                            {t('brand_detail.headquarters')}：
                            {currentBrand.headquarters ||
                              t('brand_detail.unknown')}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <CertificationBadges brandId={currentBrand.id} />

      {/* 购买入口卡片 */}
      <PurchaseEntryCard brand={currentBrand as any} />

      {currentRating ? (
        <div style={{ marginTop: 24 }}>
          <RatingDisplay rating={currentRating} />
          {/* 增强雷达图 */}
          {(() => {
            // 兼容两种维度数据结构：number 或 { score }
            const dims: Record<string, number> = {};
            const d: any = currentRating.dimensions as any;
            if (d) {
              const keys = Object.keys(d);
              keys.forEach(k => {
                const v = (d as any)[k];
                dims[k] = typeof v === 'number' ? v : (v?.score ?? 0);
              });
            }
            return (
              <div style={{ marginTop: 16 }}>
                <EnhancedRadarChart
                  brandData={{
                    environmental_score: dims.environmental || 0,
                    labor_score: dims.labor || 0,
                    animal_welfare_score: dims.animal_welfare || 0,
                    community_score: dims.community || 0,
                    transparency_score: dims.transparency || 0,
                  }}
                  showComparison={true}
                  showTrend={true}
                />
              </div>
            );
          })()}
          {/* 证据与来源 */}
          <EvidenceSection
            sources={currentRating.dataSources}
            lastUpdated={currentRating.updatedAt}
          />
        </div>
      ) : (
        <div style={{ marginTop: 24, color: '#999' }}>
          {t('brand_detail.no_rating')}
        </div>
      )}

      {/* 用户评价 */}
      <div style={{ marginTop: 24 }}>
        <UserReviews brandId={currentBrand.id} />
      </div>

      {/* 相似品牌推荐 */}
      <div style={{ marginTop: 24 }}>
        <Divider />
        <Title level={3}>{t('brand_detail.similar_brands')}</Title>

        {similarLoading ? (
          <Loading
            tip={t('brand_detail.loading_similar', {
              defaultValue: 'Loading similar brands...',
            })}
          />
        ) : similarBrands.length > 0 ? (
          <Row gutter={[16, 16]}>
            {similarBrands.map(brand => (
              <Col span={8} key={brand.id}>
                <Card
                  hoverable
                  size="small"
                  actions={[
                    <span
                      key="view"
                      onClick={() => handleViewDetails(brand.id)}
                      style={{ cursor: 'pointer', color: 'var(--app-primary-color)' }}
                    >
                      {t('brand_detail.view_details')}
                    </span>,
                  ]}
                >
                  <Card.Meta
                    title={brand.name}
                    description={
                      <div>
                        <p style={{ fontSize: '12px', marginBottom: 4 }}>
                          {brand.description.substring(0, 80)}...
                        </p>
                        {brand.similarity && (
                          <div style={{ marginTop: 8 }}>
                            <span
                              style={{
                                color: 'var(--app-primary-color)',
                                border: '1px solid var(--app-primary-color)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                background: 'transparent',
                              }}
                            >
                              {t('brand_detail.similarity')}: {Math.round(brand.similarity)}%
                            </span>
                          </div>
                        )}
                      </div>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <p style={{ color: '#666' }}>{t('brand_detail.no_similar_brands')}</p>
        )}
      </div>

      {/* 品牌对比弹窗 */}
      <BrandComparison
        visible={comparisonVisible}
        onClose={() => setComparisonVisible(false)}
        brands={comparisonBrands}
        onRemoveBrand={brandId => {
          setComparisonBrands(prev => prev.filter(b => b.id !== brandId));
        }}
      />
    </div>
  );
};

export default BrandDetailPage;
